import { useInfiniteQuery, useQuery, useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

export const MEETING_PREP_BRIEFS_QUERY_KEY = 'meetingPrep/briefs';

const DEFAULT_PAGE_SIZE = 25;

const getMeetingPrepBriefsInfiniteQueryOptions = (
	{ pageSize, meta }: { pageSize: number; meta?: LevStandardQueryMeta },
	userSession: ReturnType<typeof useUserSession>
) => ({
	getNextPageParam: (lastPage: Api.IPagedCollection<Api.IMeetingPrepBrief>) => lastPage.pageToken || undefined,
	initialPageParam: '',
	queryFn: ({ pageParam }: { pageParam: unknown }) =>
		userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IMeetingPrepBrief>>(
			Api.ImpersonationBroker.composeApiUrl({
				queryParams: { pageSize, pageToken: typeof pageParam === 'string' ? pageParam : '' },
				urlPath: 'MeetingPrep/briefs',
			}),
			'GET'
		),
	queryKey: [MEETING_PREP_BRIEFS_QUERY_KEY, pageSize],
	meta,
});

export const useMeetingPrepBriefsQuery = ({
	pageSize = DEFAULT_PAGE_SIZE,
	enabled = true,
	meta,
}: {
	pageSize?: number;
	enabled?: boolean;
	meta?: LevStandardQueryMeta;
} = {}) => {
	const userSession = useUserSession();
	return useInfiniteQuery({
		enabled,
		...getMeetingPrepBriefsInfiniteQueryOptions({ pageSize, meta }, userSession),
	});
};

export const useMeetingPrepBriefsSuspenseQuery = ({
	pageSize = DEFAULT_PAGE_SIZE,
	meta,
}: {
	pageSize?: number;
	meta?: LevStandardQueryMeta;
} = {}) => {
	const userSession = useUserSession();
	return useSuspenseInfiniteQuery(getMeetingPrepBriefsInfiniteQueryOptions({ pageSize, meta }, userSession));
};

const getMeetingPrepBriefQueryOptions = (
	{ briefId, initialData, meta }: { briefId: string; initialData?: Api.IMeetingPrepBrief; meta?: LevStandardQueryMeta },
	userSession: ReturnType<typeof useUserSession>
) => ({
	queryKey: [MEETING_PREP_BRIEFS_QUERY_KEY, 'detail', briefId],
	queryFn: () =>
		userSession.webServiceHelper.callAsync<Api.IMeetingPrepBrief>({
			path: `MeetingPrep/briefs/${encodeURIComponent(briefId)}`,
			method: 'GET',
			// Another user's brief, or one aged out past its 30-day TTL, resolves as null data rather than
			// an error — a not-found detail state, not a page failure.
			throwOn404: false,
		}),
	initialData,
	staleTime: Infinity,
	meta,
});

export const useMeetingPrepBriefQuery = ({
	briefId,
	initialData,
	meta,
}: {
	briefId: string | null;
	initialData?: Api.IMeetingPrepBrief;
	meta?: LevStandardQueryMeta;
}) => {
	const userSession = useUserSession();
	return useQuery({
		enabled: !!briefId,
		...getMeetingPrepBriefQueryOptions({ briefId: briefId ?? '', initialData, meta }, userSession),
	});
};

/**
 * Suspending variant for the detail pane. `briefId` is required rather than nullable because a suspense
 * query has no `enabled` escape hatch — the caller has to know it has one.
 */
export const useMeetingPrepBriefSuspenseQuery = ({
	briefId,
	initialData,
	meta,
}: {
	briefId: string;
	initialData?: Api.IMeetingPrepBrief;
	meta?: LevStandardQueryMeta;
}) => {
	const userSession = useUserSession();
	return useSuspenseQuery(getMeetingPrepBriefQueryOptions({ briefId, initialData, meta }, userSession));
};

export const invalidateMeetingPrepBriefs = () =>
	reactQueryClient.invalidateQueries({ queryKey: [MEETING_PREP_BRIEFS_QUERY_KEY] });
